import React from "react";

// Shared record-rendering primitives.
//
// Extracted from EntityDetailPanel so the side panel and the in-chat
// record_detail block render an identical field list — same labels, same
// value formatting, same nested-section handling. The chat block is a
// narrower presentation of the same thing, never a second implementation.

export const KIND_LABELS = {
  seller_profile: "Seller profile",
  inbox: "Inbox",
  account_list: "Account list",
  contact_list: "Contact list",
  contact: "Contact",
  account: "Account",
  sequence: "Sequence",
  task: "Task",
  template: "Template",
  team: "Team"
};

// camelCase / snake_case key → readable label (mirror of the agent side).
export const labelize = key =>
  String(key)
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();

// Keys that are DB plumbing, not information: ids, class markers, sort hints,
// internal tags, avatar/picture URLs. The panel is for reading a record, and
// no one reads "Sort Order: 1" or a googleusercontent URL.
const PLUMBING_KEY = /^_?id$|Id$|^_class$|sortOrder|picture|avatar|image|photo|Tag$|^meta|version$|^raw/i;

// Backend labels that read better as product words.
const LABEL_OVERRIDES = {
  createTs: "Created",
  updateTs: "Updated",
  lastEmailSyncedDate: "Last email synced",
  lastReplySyncedDate: "Last reply synced",
  emailId: "Email"
};

export const fieldLabel = key => LABEL_OVERRIDES[key] || labelize(key);

// Raw provider enums a user should never read verbatim; other enum-shaped
// values (single snake/upper token) get a generic title-case pass.
const ENUM_LABELS = {
  gmail_oath: "Gmail",
  gmail_oauth: "Gmail",
  outlook_oath: "Outlook",
  outlook_oauth: "Outlook",
  smtp: "SMTP",
  smtp_imap: "SMTP/IMAP"
};

const humanizeEnum = s => {
  const lower = s.toLowerCase();
  if (ENUM_LABELS[lower]) return ENUM_LABELS[lower];
  if (/^[a-z]+(_[a-z]+)*$/.test(s) || /^[A-Z]+(_[A-Z]+)*$/.test(s)) {
    return s
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, c => c.toUpperCase());
  }
  return s;
};

export const formatValue = value => {
  if (value == null || value === "") return null;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") {
    if (value > 1000000000000 && value < 3000000000000) {
      return new Date(value).toLocaleString();
    }
    return String(value);
  }
  if (Array.isArray(value)) {
    const rendered = value
      .map(entry => (typeof entry === "object" ? null : String(entry)))
      .filter(Boolean);
    return rendered.length ? rendered.join(", ") : null;
  }
  if (typeof value === "object") return null;
  const s = String(value).trim();
  // ISO timestamps → readable date; raw "2026-07-17T18:33:00.539+0000" rows
  // were most of what made the panel look like a Mongo dump.
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:/.test(s)) {
    const d = new Date(s);
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric"
      });
    }
  }
  // Bare URLs read as noise in a label/value row — the card face or a link
  // control is where a URL belongs, not a wrapped four-line string.
  if (/^https?:\/\/\S+$/.test(s) && s.length > 60) return null;
  return humanizeEnum(s);
};

// Per-kind curation for records whose raw shape is a settings dump. A team
// carries ~140 keys (every quota pair + ~60 feature flags); nobody opened the
// panel to read "Zapier Integration Enabled: Yes". Curated kinds show a few
// composed rows — usage as "used of eligible" — with the rest behind Show all.
const CURATED_ROWS = {
  team: [
    { key: "email" },
    { key: "teamStatus", label: "Status" },
    { pair: ["noOfSeatsUsed", "noOfSeatsEligible"], label: "Seats" },
    { pair: ["noOfInboxesUsed", "noOfInboxesEligible"], label: "Inboxes" },
    { pair: ["noOfSequencesUsed", "noOfSequencesEligible"], label: "Sequences" },
    { pair: ["noOfContactsUsed", "noOfContactsEligible"], label: "Contacts" },
    { pair: ["creditsUsed", "creditsEligible"], label: "Credits" },
    { pair: ["emailCreditsUsed", "emailCreditsEligible"], label: "Email credits" },
    {
      pair: ["deepResearchCreditsUsed", "deepResearchCreditsEligible"],
      label: "Deep Research credits"
    },
    { key: "dailySendLimit", label: "Daily send limit" },
    { key: "createTs" },
    { key: "updateTs" }
  ]
};

const numFmt = value =>
  typeof value === "number" ? value.toLocaleString("en-US") : formatValue(value);

/**
 * Curated rows for a kind, or null when the kind has no curation.
 * Pairs render as "used of eligible"; rows whose keys are absent are skipped,
 * so a slimmer payload never renders "undefined of undefined".
 */
export const curatedRows = (record, kind) => {
  const spec = CURATED_ROWS[kind];
  if (!spec || !record) return null;
  const rows = [];
  spec.forEach(entry => {
    if (entry.pair) {
      const [usedKey, eligibleKey] = entry.pair;
      if (record[usedKey] == null && record[eligibleKey] == null) return;
      rows.push([
        entry.label,
        `${numFmt(record[usedKey] ?? 0)} of ${numFmt(record[eligibleKey] ?? 0)}`
      ]);
      return;
    }
    const rendered = formatValue(record[entry.key]);
    if (rendered !== null) rows.push([entry.label || fieldLabel(entry.key), rendered]);
  });
  return rows.length ? rows : null;
};

/** Split a record into flat rows + one-level-deep nested sections. */
export const splitRecord = record => {
  const rows = [];
  const sections = [];
  Object.entries(record || {}).forEach(([key, value]) => {
    // Overrides are an allowlist: emailId is data ("Email"), not an id field,
    // even though the plumbing regex matches its suffix.
    if (PLUMBING_KEY.test(key) && !LABEL_OVERRIDES[key]) return;
    if (value && typeof value === "object" && !Array.isArray(value)) {
      sections.push([key, value]);
      return;
    }
    const rendered = formatValue(value);
    if (rendered !== null) rows.push([key, rendered]);
  });
  return { rows, sections };
};

/**
 * Generic key-value renderer for whatever record a tool returned — warming
 * status, limits, signature, connected sequences: if it came back, it shows
 * here, without this component knowing any schema. Nested objects render as
 * their own titled section, one level deep.
 *
 * `limit` caps the flat rows (the in-chat card shows a preview and lets the
 * user expand); the panel passes no limit and shows everything.
 */
// Fields the CARD HEADER already shows. Repeating them in the body gave a
// contact card that opened "Lane / Contact · Support Lead" and then spent its
// first three rows saying Name: Lane, First Name: Lane, Last Name: NoCompany --
// three rows of nothing before the first useful fact.
const HEADER_FIELDS = new Set([
  "name", "firstname", "lastname", "fullname",
  "jobtitle", "title", "subject", "status", "stage"
]);

export const DetailRows = ({ record, limit, kind, skipHeaderFields = false }) => {
  const [showAll, setShowAll] = React.useState(false);
  const split = splitRecord(record);
  const sections = split.sections;
  const rows = skipHeaderFields && !showAll
    ? split.rows.filter(([k]) => !HEADER_FIELDS.has(String(k).toLowerCase().replace(/[_\s]/g, "")))
    : split.rows;
  // A curated kind answers with a dozen composed rows; the raw dump stays one
  // click away rather than being the default reading experience.
  const curated = !showAll && curatedRows(record, kind);
  const flatRows = curated || rows;
  const shownRows = limit ? flatRows.slice(0, limit) : flatRows;
  const shownSections = limit || curated ? [] : sections;
  const hiddenCount = curated ? rows.length - curated.length : 0;

  return (
    <>
      <div className="copilotEntityDetail__rows">
        {shownRows.map(([key, rendered]) => (
          <div key={key} className="copilotEntityDetail__row">
            <span className="copilotEntityDetail__rowLabel">
              {curated ? key : fieldLabel(key)}
            </span>
            <span className="copilotEntityDetail__rowValue">{rendered}</span>
          </div>
        ))}
      </div>
      {curated && hiddenCount > 0 && !limit && (
        <button
          type="button"
          className="copilotEntityDetail__showAll"
          onClick={() => setShowAll(true)}
        >
          Show all {rows.length} fields
        </button>
      )}
      {shownSections.map(([key, value]) => (
        <div key={key} className="copilotEntityDetail__section">
          <div className="copilotEntityDetail__sectionTitle">{labelize(key)}</div>
          <div className="copilotEntityDetail__rows">
            {Object.entries(value).map(([childKey, childValue]) => {
              const rendered = formatValue(childValue);
              if (rendered === null) return null;
              return (
                <div key={childKey} className="copilotEntityDetail__row">
                  <span className="copilotEntityDetail__rowLabel">{fieldLabel(childKey)}</span>
                  <span className="copilotEntityDetail__rowValue">{rendered}</span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </>
  );
};

export default DetailRows;
